import { PLAN_MATCH_PATH } from "./activityCreate";

/**
 * Issue #1149 — what Plan's Coming up section shows: the rows and the four
 * counts, built from `GET /activities/upcoming` (matches and training, soonest
 * first) plus the player's own goals. The API has no goals (its `development`
 * count is always 0), and a goal belongs here only when it has a target date
 * (the board's sticky), so the goals come from the list `/development` has
 * already loaded.
 *
 * Pure so the ordering and the "undated goal is not listed" rule are pinned
 * without a mount (`comingUp.test.js`). The render is `PlanComingUp.jsx`.
 */
export { PLAN_MATCH_PATH };

/** The training list: where the overdue prompt (#1041) and a training row open. */
export const TRAINING_LIST_PATH = "/learn?tab=training";

/**
 * How many planned training sessions are past due and have no reflection yet,
 * from `GET /activities/upcoming`. Same rule as the old Dashboard
 * (`Dashboard.jsx`): the summary count, else the list length, else 0.
 */
export function overdueTrainingCount(data) {
  return data?.summary?.needs_reflection ?? (data?.needs_reflection?.length || 0);
}

const KIND_ORDER = { match: 0, training: 1, goal: 2 };
const TAGS = { match: "Match", training: "Training", goal: "Goal" };

/** `YYYY-MM-DD` plus `days`, calendar arithmetic only (no zone drift). */
function addDays(iso, days) {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * How far ahead a match can be planned (the Plan a match Date field's `max`),
 * and the horizon Coming up asks the API for: one day more, because the API's
 * window is `date <= today + days` in the caller's day and the form's last day
 * must be inside it. The two come from one place so the form and the list
 * cannot disagree (#1240 F1).
 */
export const PLAN_MAX_DAYS_AHEAD = 365;
export const COMING_UP_HORIZON_DAYS = PLAN_MAX_DAYS_AHEAD + 1;

/** The last day the Plan a match Date field accepts, given today's `YYYY-MM-DD`. */
export function planDateMax(todayIsoString) {
  return addDays(todayIsoString, PLAN_MAX_DAYS_AHEAD);
}

/** `2027-10-10` as `Sun 10 Oct 2027`, for the planning-window messages. */
export function planDateLabel(iso) {
  return new Date(`${iso}T00:00:00Z`)
    .toLocaleDateString("en-NZ", { weekday: "short", day: "numeric", month: "short", year: "numeric", timeZone: "UTC" })
    .replace(",", "");
}

/**
 * Issue #1245 (D263): a PLANNED training session is capped at today + 365
 * days, the same last day as Plan a match, because a session dated later
 * saves and never shows in Coming up. Past sessions stay unlimited, so this
 * only looks at the far end. `null` is fine (or no date: the required-field
 * rule owns that); otherwise the sentence for the Date field. The API refuses
 * the same dates (`api/src/lib/planWindow.ts`).
 */
export function plannedSessionDateError(date, todayIsoString) {
  if (!date) return null;
  const max = planDateMax(todayIsoString);
  if (date <= max) return null;
  return `A planned session can be at most a year ahead. Pick a date on or before ${planDateLabel(max)}.`;
}

/** `HH:MM` as `5:30 pm`; "" for no time. */
export function clock12(time) {
  if (!time) return "";
  const [h, m] = time.split(":").map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return "";
  const suffix = h >= 12 ? "pm" : "am";
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(m).padStart(2, "0")} ${suffix}`;
}

/** "Today", "Tomorrow", else "Sat 17 Oct", then " · 10:00 am" when timed. */
export function whenLabel(date, time, today) {
  let day;
  if (date === today) day = "Today";
  else if (date === addDays(today, 1)) day = "Tomorrow";
  else {
    day = new Date(`${date}T00:00:00Z`).toLocaleDateString("en-NZ", {
      weekday: "short",
      day: "numeric",
      month: "short",
      timeZone: "UTC",
    });
    // en-NZ may render "Sat, 17 Oct"; the board has no comma.
    day = day.replace(",", "");
  }
  const t = clock12(time);
  return t ? `${day} · ${t}` : day;
}

/**
 * Issue #1319 (Pick XI player; D302) — the chip a coach's game carries in
 * Coming up. A player's own planned match carries none.
 */
export const COACH_GAME_CHIP = "Scheduled by your coach";

/** Where a coach's game opens: the game page, a step of `/development`. */
export const coachGamePath = (gameId) => `/development?game=${encodeURIComponent(gameId)}`;

export function buildComingUp({ items = [], goals = [], today, games = [] }) {
  const rows = [];
  for (const i of items) {
    if (i.date < today) continue;
    rows.push({
      key: `${i.kind}-${i.id}`,
      kind: i.kind,
      date: i.date,
      time: i.time || "",
      title: i.title,
      place: i.location || "",
      tag: TAGS[i.kind] || "",
      // A planned match has nowhere to open while it is still to come; once its
      // day has passed it is the overdue card instead (#1150, `PlanComingUp`).
      // A training row opens its own list.
      to: i.kind === "match" ? null : i.link || null,
    });
  }
  // The coach's games (`/coach-games/mine`), only when the caller passes them.
  for (const g of games) {
    if (!g.match_date || g.match_date < today) continue;
    rows.push({
      key: `coach-game-${g.game_id}`,
      kind: "match",
      date: g.match_date,
      time: g.match_time || "",
      title: `Match vs ${g.opponent}`,
      place: [g.ground, g.team_announced ? "team announced" : ""].filter(Boolean).join(" \u00b7 "),
      tag: TAGS.match,
      chip: COACH_GAME_CHIP,
      to: coachGamePath(g.game_id),
    });
  }
  for (const g of goals) {
    if (!g.target_date || g.status === "completed" || g.target_date < today) continue;
    rows.push({
      key: `goal-${g.goal_id}`,
      kind: "goal",
      date: g.target_date,
      time: "",
      title: g.goal_title || g.target || "Goal",
      place: g.horizon === "long" ? "Long-term goal" : "Short-term goal",
      tag: TAGS.goal,
      to: `/development?tab=${g.horizon === "long" ? "long" : "short"}`,
    });
  }
  rows.sort(
    (a, b) =>
      a.date.localeCompare(b.date) ||
      (a.time || "99:99").localeCompare(b.time || "99:99") ||
      KIND_ORDER[a.kind] - KIND_ORDER[b.kind],
  );
  const weekEnd = addDays(today, 7);
  return {
    rows,
    counts: {
      thisWeek: rows.filter((r) => r.date <= weekEnd).length,
      matches: rows.filter((r) => r.kind === "match").length,
      training: rows.filter((r) => r.kind === "training").length,
      goals: rows.filter((r) => r.kind === "goal").length,
    },
  };
}

/**
 * Issue #1245: should the dialog's "fill in the required fields" toast show for
 * this set of field errors? Not when the only error is the date-range sentence
 * (`rangeRefused`): that is not a missing field, and the sentence under the Date
 * field already says what to do.
 */
export function refusalToast(errors, rangeRefused) {
  const keys = Object.keys(errors).filter((k) => errors[k]);
  if (keys.length === 0) return false;
  return !(rangeRefused && keys.length === 1 && keys[0] === "training_date");
}
